<template>
  <base-layout>
    <base-dashboard
      :is-loading="isLoadingPage"
    >
      <b-row
        class="gutters-0 header px-5 align-items-center"
      >
        <b-col
          cols="auto"
          class="p-0"
        >
          <b-button
            variant="tertiary"
            class="d-flex align-items-center"
            @click="redirectToContentPage()"
            data-test="article-back-button"
          >
            <i class="fal fa-arrow-left mr-2" />
            <span>Back</span>
          </b-button>
        </b-col>
        <b-col
          cols="auto"
          class="p-0 w-75"
        >
          <h3
            id="article-header-title"
            class="mb-0 ml-3 mr-5"
            data-test="article-header-title"
          >
            Editing: {{ articleHeader }}
          </h3>
        </b-col>
        <b-col
          cols="auto"
          class="p-0"
        >
          <p
            :class="`text-nowrap mb-0 ${getStateClass(article.state)}`"
            data-test="article-header-state"
          >
            {{ article.state }}
          </p>
        </b-col>
      </b-row>
      <section
        class="page-section first-element"
      >
        <b-row
          class="gutters-0 new-gutters-sm align-items-end w-100"
        >
          <b-col
            cols="auto"
            class="flex-grow-1"
          >
            <b-form-group
              id="article-title"
              class="mb-0"
            >
              <template
                v-slot:label
                label-for="article-title-input"
              >
                <h4
                  class="mb-0"
                >
                  Content Title
                </h4>
              </template>
              <b-form-input
                id="article-title-input"
                :value="articleTitle"
                @input="updateArticleTitle($event)"
                :disabled="!canUpdateArticle"
                data-test="article-title-input"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
          >
            <b-form-group
              id="url"
              class="mb-0"
            >
              <template
                v-slot:label
                label-for="article-url-input"
              >
                <h4
                  class="mb-0"
                >
                  URL
                </h4>
              </template>
              <b-input-group>
                <b-form-input
                  id="article-url-input"
                  class="border-right-0 pr-0"
                  :value="article.url"
                  data-test="article-url-input"
                  disabled
                />
                <b-input-group-append
                  class="m-0"
                >
                  <b-button
                    id="url-open-button"
                    size="sm"
                    variant="tertiary"
                    class="m-0 border-left-0 btn-link"
                    @click="openURLInNewTab(article.url)"
                  >
                    <i class="fal fa-arrow-up-right-from-square" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            cols="2"
            class="mb-0"
          >
            <b-form-select
              :options="languages"
              :value="articleLanguageId"
              @change="selectLanguage($event)"
              text-field="name"
              value-field="id"
              :disabled="!canUpdateArticle"
              data-test="article-language-dropdown"
            >
              <b-form-select-option
                :value="null"
                name="language"
                class="d-none"
                disabled
              >
                Select Language
              </b-form-select-option>
            </b-form-select>
          </b-col>
          <b-col
            cols="auto"
          >
            <b-button
              variant="primary"
              :class="{ 'btn-loading': isLoadingSaveButton }"
              @click="saveChanges()"
              :disabled="!canSaveChanges"
              data-test="article-save-button"
            >
              Save
            </b-button>
            <b-dropdown
              variant="plain"
              toggle-class="text-decoration-none"
              no-caret
              :disabled="!canOpenKebabMenu"
              data-test="article-actions-dropdown-button"
            >
              <template
                v-slot:button-content
                class="m-0 p-0"
              >
                <i class="fa-light fa-ellipsis-v" />
              </template>
              <b-dropdown-item
                @click="openActionConfirmationModal('publish')"
                :disabled="!canPublishArticles({
                  ids: [article.id],
                })"
                data-test="publish-article"
              >
                <template v-if="GET_IS_MANAGED_CAMPAIGN">
                  Publish
                </template>
                <template v-else>
                  Send for Approval
                </template>
              </b-dropdown-item>
              <b-dropdown-item
                @click="openActionConfirmationModal('delete')"
                :disabled="!canDeleteArticles({
                  ids: [article.id],
                })"
                data-test="delete-article"
              >
                <p
                  :class="`text-danger${!canDeleteArticles({
                    ids: [article.id],
                  }) ? '-muted' : ''} mb-0`"
                >
                  Delete
                </p>
              </b-dropdown-item>
              <div
                v-if="canChangeApprovalState({
                  ids: [article.id],
                })"
              >
                <b-dropdown-item
                  @click="openActionConfirmationModal('approve')"
                  :disabled="canChangeArticlesLanguage({
                    ids: [article.id],
                  })"
                  data-test="approve-article"
                >
                  Approve
                </b-dropdown-item>
                <b-dropdown-item
                  @click="openActionConfirmationModal('disapprove')"
                  :disabled="canChangeArticlesLanguage({
                    ids: [article.id],
                  })"
                  data-test="disapprove-article"
                >
                  Disapprove
                </b-dropdown-item>
              </div>
            </b-dropdown>
          </b-col>
        </b-row>
      </section>
      <section
        class="page-section"
      >
        <b-form-group
          id="full-article"
          class="mb-0"
        >
          <template
            v-slot:label
            label-for="full-article-input"
          >
            <div class="d-flex align-items-center">
              <base-help-tooltip>
                <template v-slot:left>
                  <h4
                    class="mb-0"
                  >
                    Full Article
                  </h4>
                </template>
                <template v-slot:popover-body>
                  Text entered here is used for matching your adverts to relevant publisher articles when your campaign is live. Editing text here will not affect what is displayed in your advertising creatives.
                </template>
              </base-help-tooltip>
              <div class="ml-auto d-flex align-items-center">
                <base-help-tooltip>
                  <template v-slot:left>
                    <h6
                      class="mb-0"
                    >
                      Matching Health
                    </h6>
                  </template>
                  <template v-slot:popover-body>
                    Higher word count improves contextual matching. Please enter at least 50 words of content to ensure good matching accuracy.
                  </template>
                </base-help-tooltip>
                <div
                  id="matching-health-bars-container"
                  class="d-flex justify-content-between ml-4"
                >
                  <div :class="`bar bar-1 ${getBarColour(1)}`" />
                  <div :class="`bar bar-2 ${getBarColour(2)}`" />
                  <div :class="`bar bar-3 ${getBarColour(3)}`" />
                </div>
              </div>
            </div>
          </template>
          <div
            id="full-article-container"
            ref="full-article-container"
            :class="['form-control h-100 p-0', { 'focused': isFullArticleInputFocused }]"
          >
            <b-form-textarea
              id="full-article-input"
              ref="full-article-input"
              :value="articleContent"
              @input="updateArticleContent($event)"
              @focus="isFullArticleInputFocused = true"
              @blur="isFullArticleInputFocused = false"
              placeholder=""
              rows="12"
              max-rows="12"
              no-resize
              :disabled="!canUpdateArticle"
              data-test="full-article-input"
            />
            <p
              id="full-article-word-count"
              class="mb-0 p-2"
              @click="focusInput('full-article-input')"
              data-test="full-article-input-word-count"
            >
              Word Count: {{ articleWordCount }}
            </p>
          </div>
        </b-form-group>
      </section>
      <section
        class="page-section last-element"
      >
        <b-row
          class="gutters-0 new-gutters-sm"
        >
          <b-col
            cols="auto"
          >
            <p
              class="mb-0 text-muted small"
              data-test="article-created-date"
            >
              Creation Date: {{ articleCreatedDate }}
            </p>
          </b-col>
          <b-col
            cols="auto"
          >
            <p
              class="mb-0 text-muted small"
              data-test="article-updated-date"
            >
              Last Updated: {{ articleUpdatedDate }}
            </p>
          </b-col>
          <b-col
            v-if="article.hasOwnProperty('encodedExternalId')"
            cols="auto"
          >
            <p
              class="mb-0 text-muted small"
              data-test="article-encodedExternalId"
            >
              Content ID: {{ article.encodedExternalId }}
            </p>
          </b-col>
        </b-row>
      </section>
      <actions-confirmation-modal
        :custom-props="confirmationModalCustomProps"
        @confirmAction="confirmAction()"
        @resetCustomProps="resetCustomProps()"
      />
    </base-dashboard>
  </base-layout>
</template>
<script>
/* eslint-disable prefer-destructuring */
/* eslint-disable no-await-in-loop */
import {
  mapState, mapActions, mapMutations, mapGetters,
} from 'vuex';
import * as rootTypes from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import helper from '@/core/helper';
import dateHelper from '@/core/mixins/dateHelper';
import urlHelper from '@/core/mixins/urlHelper';
import textHelper from '@/core/mixins/textHelper';
import ArticlesStateMixins from '@/modules/content/components/ContentPage/mixins/ArticlesStateMixins';
import ArticlesActionsMixins from '@/modules/content/components/ContentPage/mixins/ArticlesActionsMixins';
import ActionsConfirmationModal from '@/modules/content/components/ContentPage/ActionsConfirmationModal';

export default {
  name: 'ContentEditor',
  mixins: [
    dateHelper,
    urlHelper,
    textHelper,
    ArticlesStateMixins,
    ArticlesActionsMixins,
  ],
  components: {
    ActionsConfirmationModal,
  },
  data() {
    return {
      isLoadingPage: false,
      isLoadingSaveButton: false,
      isFullArticleInputFocused: false,
      article: {},
      articleChanges: {},
      confirmationModalCustomProps: {},
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'selectedAdvertiser',
    ]),
    ...mapState('content', {
      scrapedContent: 'newScrapedContent',
    }),
    ...mapState('content', [
      'languages',
      'feedId',
      'scrapingBatchId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
      campaignTypes.GET_IS_READ_ONLY,
    ]),
    articleHeader() {
      return this.isArticleFailed(this.article) ? this.article.url : this.article.title;
    },
    articleTitle() {
      let title = '';
      if (this.articleChanges.hasOwnProperty('title')) title = this.articleChanges.title;
      else title = this.article.title;
      this.SET_CONTENT_EDITOR_ARTICLE({ title });
      return title;
    },
    articleLanguageId() {
      let languageId = null;
      if (this.articleChanges.hasOwnProperty('languageId')) languageId = this.articleChanges.languageId;
      else if (!this.isArticleFailed(this.article)) languageId = this.article.languageId;
      this.SET_CONTENT_EDITOR_ARTICLE({ languageId });
      return languageId;
    },
    articleContent() {
      let contentBody = '';
      if (this.articleChanges.hasOwnProperty('contentBody')) contentBody = this.articleChanges.contentBody;
      else contentBody = this.article.contentBody;
      this.SET_CONTENT_EDITOR_ARTICLE({ contentBody });
      return contentBody;
    },
    articleWordCount() {
      if (!this.articleContent) return 0;
      return this.articleContent.split(/\s+/g).length;
    },
    articleCreatedDate() {
      return this.formatDateForPlatform(this.article.createdDate);
    },
    articleUpdatedDate() {
      return this.isArticleFailed(this.article) ? this.articleCreatedDate : this.formatDateForPlatform(this.article.updatedDate);
    },
    articleMatchingHealth() {
      let matchingHealth = 'LOW';
      if (this.articleWordCount < 50) matchingHealth = 'LOW';
      else if (this.articleWordCount < 100) matchingHealth = 'MEDIUM';
      else matchingHealth = 'HIGH';
      this.SET_CONTENT_EDITOR_ARTICLE({ matchingHealth });
      return matchingHealth;
    },
    canUpdateArticle() {
      return !this.GET_IS_READ_ONLY()
      && ['DRAFT', 'PAUSED', 'FAILED'].includes(this.article.state);
    },
    canSaveChanges() {
      return this.canUpdateArticle
        && this.articleMatchingHealth !== 'LOW'
        && (this.articleTitle && this.articleTitle !== '')
        && (this.article.hasOwnProperty('url') && this.article.url !== '')
        && this.articleLanguageId
        && helper.objLength(this.articleChanges) > 0;
    },
    canOpenKebabMenu() {
      return (!this.GET_IS_READ_ONLY()
      && !['PUBLISHED', 'PAUSED', 'SCHEDULED', 'APPROVAL'].includes(this.article.state))
      || this.canChangeApprovalState({
        ids: [this.article.id],
      });
    },
  },
  methods: {
    ...mapMutations([
      rootTypes.SET_GLOBAL_MESSAGE,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_ADVERTISER,
      campaignTypes.FETCH_EXISTING_CAMPAIGN,
    ]),
    ...mapActions('content', [
      contentTypes.NEW_FETCH_CONTENT_SCRAPES_ONCE,
      contentTypes.FETCH_LANGUAGES,
      contentTypes.FETCH_CONTENT_ITEM,
      contentTypes.SET_CONTENT_EDITOR_ARTICLE,
      contentTypes.SAVE_CONTENT_EDITOR_CHANGES,
    ]),
    ...mapMutations('content', [
      contentTypes.SET_FEED_ID,
    ]),
    updateArticleTitle: helper.debounce(function updateArticleTitle(title) {
      if (!this.articleChanges.hasOwnProperty('title')) this.$set(this.articleChanges, 'title', title);
      else if (title === this.article.title) this.$delete(this.articleChanges, 'title');
      else this.articleChanges.title = title;
    }, 500),
    selectLanguage(languageId) {
      if (!this.articleChanges.hasOwnProperty('languageId')) this.$set(this.articleChanges, 'languageId', languageId);
      else if (languageId === this.article.languageId) this.$delete(this.articleChanges, 'languageId');
      else this.articleChanges.languageId = languageId;
    },
    updateArticleContent: helper.debounce(function updateArticleContent(contentBody) {
      if (!this.articleChanges.hasOwnProperty('contentBody')) {
        this.$set(this.articleChanges, 'contentBody', contentBody);
        this.SET_CONTENT_EDITOR_ARTICLE({ contentBody });
      } else if (this.isEqualString(contentBody, this.article.contentBody)) {
        this.$delete(this.articleChanges, 'contentBody');
        this.SET_CONTENT_EDITOR_ARTICLE({ contentBody: this.article.contentBody });
      } else {
        this.articleChanges.contentBody = contentBody;
        this.SET_CONTENT_EDITOR_ARTICLE({ contentBody });
      }
    }, 500),
    openActionConfirmationModal(action) {
      const payload = {
        action,
        ids: [this.article.id],
      };
      if (this.articleChanges.hasOwnProperty('languageId')) payload.newLanguageId = this.articleChanges.languageId;
      this.confirmationModalCustomProps = Object.assign({}, payload);
      this.$root.$emit('bv::show::modal', 'actions-confirm-modal');
    },
    async confirmAction() {
      if (this.confirmationModalCustomProps.action === 'delete') {
        return this.redirectToContentPage();
      }
      this.populateSuccessfulArticle(this.article.id);
    },
    resetCustomProps() {
      this.confirmationModalCustomProps = {};
    },
    resetArticleChanges() {
      this.$set(this.$data, 'articleChanges', {});
    },
    saveChanges() {
      this.isLoadingSaveButton = true;
      if (!this.canSaveChanges) {
        this.isLoadingSaveButton = false;
        return this.SET_GLOBAL_MESSAGE({
          message: 'You are not allowed to save changes',
          type: 'danger',
        });
      }
      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        state: this.article.state,
        body: {
          title: this.articleTitle,
          contentBody: this.articleContent,
          languageId: this.articleLanguageId,
        },
      };
      if (!this.isArticleFailed(this.article)) {
        payload.id = this.article.id;
      } else {
        payload.contentScrapeId = this.scrapingBatchId;
        payload.contentScrapeItemId = this.article.id;
        payload.body.url = this.article.url;
        payload.body.fullText = this.articleContent;
      }
      this.SAVE_CONTENT_EDITOR_CHANGES(payload)
        .then(async (contentItem) => {
          this.isLoadingSaveButton = false;
          this.isLoadingPage = true;
          const articleId = this.isArticleFailed(this.article) ? contentItem.contentItemId : contentItem.id;
          if (this.isArticleFailed(this.article)) {
            this.$router.push({
              name: 'ContentEditor',
              params: {
                advertiserId: this.campaign.advertiserId,
                campaignId: this.campaign.id,
                feedId: this.feedId,
                articleId,
              },
            });
          }
          await this.populateSuccessfulArticle(articleId);
          this.isLoadingPage = false;
          this.resetArticleChanges();
        });
    },
    populateFailedArticle(scrapedItem) {
      this.article = {
        ...scrapedItem,
        state: 'FAILED',
        title: '',
        contentBody: '',
        languageId: null,
        matchingHealth: 'LOW',
      };
      this.SET_CONTENT_EDITOR_ARTICLE(this.article);
    },
    async populateSuccessfulArticle(articleId) {
      this.article = await this.FETCH_CONTENT_ITEM({
        advertisersId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        contentId: articleId,
      });
      this.SET_CONTENT_EDITOR_ARTICLE(this.article);
    },
    getStateClass(state) {
      const stateList = {
        FAILED: 'label label-danger px-2',
        DRAFT: 'label label-dark px-2',
        APPROVAL: 'label label-info px-2',
        SCHEDULED: 'label label-blue px-2',
        PUBLISHED: 'label label-success px-2',
        PAUSED: 'label label-warning px-2',
      };
      if (!stateList.hasOwnProperty(state)) return;
      return stateList[state];
    },
    getBarColour(number) {
      if (this.articleMatchingHealth === 'LOW') return `bg-danger${number > 1 ? '-lighter' : ''}`;
      if (this.articleMatchingHealth === 'MEDIUM') return `bg-warning${number > 2 ? '-lighter' : ''}`;
      return 'bg-success';
    },
    focusInput(ref) {
      this.$refs[ref]?.$el?.focus();
    },
    redirectToContentPage() {
      this.$router.push({
        name: this.GET_IS_CAMPAIGN_DRAFT ? 'NewCampaign' : 'EditCampaign',
        params: {
          advertiserId: this.selectedAdvertiser.id,
          campaignId: this.campaign.id,
          targetPage: 1,
        },
      });
    },
  },
  async created() {
    const {
      advertiserId, campaignId, feedId, articleId,
    } = this.$route.params;
    this.isLoadingPage = true;
    this.SET_FEED_ID(feedId);
    Promise.all([
      this.FETCH_ADVERTISER(advertiserId),
      this.FETCH_EXISTING_CAMPAIGN({ advertiserId, campaignId }),
      this.FETCH_LANGUAGES(),
      this.NEW_FETCH_CONTENT_SCRAPES_ONCE({ advertiserId, feedId }),
    ])
      .then(async () => {
        for (let index = 0; index < this.scrapedContent.length; index++) {
          const element = this.scrapedContent[index];
          if (articleId.toString() === element.id.toString()) {
            this.populateFailedArticle(element);
            break;
          }
          if (element.contentItemId && (articleId.toString() === element.contentItemId.toString())) {
            await this.populateSuccessfulArticle(articleId);
            break;
          }
        }
        this.isLoadingPage = false;
      })
      .catch(() => {
        this.redirectToContentPage();
      });
  },
  beforeDestroy() {
    this.SET_CONTENT_EDITOR_ARTICLE({});
  },
  beforeRouteLeave(to, from, next) {
    if (helper.objLength(this.articleChanges) === 0) next();
    const h = this.$createElement;
    this.$bvModal.msgBoxConfirm(h('p', [
      'You have unsaved changes!',
      h('br'),
      'Press "Proceed" to continue without saving, or "Cancel" to go back and review your changes',
    ]), {
      title: h('div', { class: 'd-flex align-items-center' }, [
        h('span', { class: 'icon mr-2' }, [
          h('i', { class: 'fas fa-exclamation-triangle warning' }),
        ]),
        h('p', { class: 'mb-0' }, 'Leave Page'),
      ]),
      size: 'sm',
      buttonSize: 'sm',
      okTitle: 'Proceed',
      okVariant: 'danger',
      cancelTitle: 'Cancel',
      cancelVariant: 'tertiary',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true,
    })
      .then((value) => {
        if (!value) return next(false);
        return next();
      })
      .catch(() => next(false));
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .page-section {
    padding: 0.75rem 1.5rem;
    &.first-element {
      padding-top: 1.5rem;
    }
    &.last-element {
      padding-bottom: 1.5rem;
    }
  }
}

#article-header-title {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-height: 1.2em;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

#url-open-button {
  background-color: $gray-100;
  &:hover, &:focus {
    background-color: $gray-200;
  }
}

#full-article-container {
  &.focused {
    border-color: $link;
    outline: .2rem solid rgba(0, 123, 255, 0.25);
  }
}

#full-article-input {
  background-color: transparent;
  border: unset;
  &:focus {
    outline: none;
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}

#full-article-word-count {
  text-align: end;
}

$bar-width: 2.25rem;
#matching-health-bars-container {
  width: calc(#{$bar-width} * 3.5);
}

.bar {
  width: $bar-width;
  height: 5px;
  border-radius: .5rem;
}
</style>
